import { redirect } from '@tanstack/react-router';

/**
 * RUOLI E ATTERRAGGI — versione allineata al realm Keycloak reale.
 *
 * Fonte unica dei nomi: i ruoli del realm (`lib/auth/rbac.ts`). Questo file
 * NON introduce nomi propri: mappa i ruoli reali sulle aree della piattaforma.
 *
 * Sostituisce integralmente la versione con nomi di comodo del documento
 * "UranIA PA — Front end: pagina dei servizi e ambiente immersivo" §4.2
 * (`adg`/`beneficiario`/`admin`), corretta dal relativo addendum: quei nomi
 * non esistevano nel realm.
 *
 *   /                     vetrina pubblica (PublicLanding) per chi non è autenticato;
 *                         se autenticato, smista verso l'area del proprio ruolo
 *        │
 *   /login/pa             identità digitale (SPID / CIE / CNS)
 *        │
 *        ├─ funzionario_adg ─┐
 *        ├─ beneficiario_ri ─┼──▶  /servizi     atterraggio: che cos'è UranIA + tutti i servizi
 *        │                   │           ├──▶  /dashboard   il mio lavoro (widget operativi)
 *        │                   │           └──▶  /immersivo   «Da dove partiamo» → editor
 *        │
 *        ├─ platform_admin ──┐
 *        └─ tenant_admin ────┴──▶  /console     cruscotto di amministrazione
 *
 * Il funzionario non vede il cruscotto e l'amministratore non atterra sui
 * servizi: sono due mestieri diversi. Chi ha entrambi i ruoli trova il
 * collegamento nell'intestazione, non un atterraggio ambiguo (vedi
 * `lib/auth/ruolo-corrente.ts` per l'ordine di priorità quando un principal
 * ha più ruoli).
 */

/** Ruoli del realm. Nessun alias inventato: se un ruolo non esiste nel realm,
 *  non compare qui. */
export type RuoloRealm = 'funzionario_adg' | 'beneficiario_ri' | 'platform_admin' | 'tenant_admin';

/**
 * 'auditor' NON esiste ancora nel realm (verificato in
 * infra/staging/keycloak-realm-urania-staging.json). Il concetto è previsto dal
 * progetto — accesso in sola lettura al fascicolo di tracciabilità, assurance
 * L3 via CNS — ma finché il ruolo non è creato non va simulato: nessuna guardia
 * lo autorizza. Quando verrà creato, aggiungerlo a `RuoloRealm`, ad
 * `ATTERRAGGIO` (`/servizi`) e a `PERMESSI['/servizi']` e
 * `PERMESSI['/dashboard']` — non a `/immersivo`, perché l'auditor non redige atti.
 */
export const RUOLI_PREVISTI_NON_ATTIVI = ['auditor'] as const;

export const ATTERRAGGIO: Record<RuoloRealm, string> = {
  funzionario_adg: '/servizi',
  beneficiario_ri: '/servizi',
  platform_admin: '/admin',
  tenant_admin: '/admin',
};

/** Ruoli ammessi per area. Restrittivo di proposito su /immersivo: redige atti
 *  solo chi ne ha titolo. `tenant_admin` va su /admin come `platform_admin`:
 *  le differenze fra i due (es. pubblicare uno snapshot del corpus, tetti di
 *  spesa di altri tenant) si gestiscono dentro il cruscotto, non sull'atterraggio. */
export const PERMESSI: Record<string, RuoloRealm[]> = {
  '/servizi': ['funzionario_adg', 'beneficiario_ri', 'platform_admin', 'tenant_admin'],
  '/dashboard': ['funzionario_adg', 'beneficiario_ri', 'platform_admin', 'tenant_admin'],
  '/immersivo': ['funzionario_adg', 'platform_admin'],
  '/admin': ['platform_admin', 'tenant_admin'],
};

/** Guardia da usare nel beforeLoad delle rotte protette. */
export function guardia(area: string, ruolo: RuoloRealm | null): void {
  if (!ruolo) throw redirect({ to: '/login/pa' });
  const ammessi = PERMESSI[area];
  // Chi non è ammesso torna alla PROPRIA area, non a una schermata di errore.
  if (ammessi && !ammessi.includes(ruolo)) throw redirect({ to: ATTERRAGGIO[ruolo] });
}

/** Smistamento della rotta radice: la vetrina pubblica resta per gli anonimi. */
export function smistaRadice(ruolo: RuoloRealm | null): string | null {
  return ruolo ? ATTERRAGGIO[ruolo] : null; // null = mostra PublicLanding
}

/**
 * Guardie di SEGMENTO (layout pathless `_pubblico`/`_servizi`/
 * `_amministrazione` — "UranIA PA — Riordino della mappa delle rotte",
 * Rilascio A passo A.1). A differenza di `guardia()` sopra — che verifica
 * un'area puntuale via `PERMESSI` — queste tre coprono l'intero segmento;
 * le restrizioni più strette di una singola rotta dentro il segmento
 * (es. `/immersivo`) restano dichiarate lì, in aggiunta.
 */

/** (pubblico): chi è già autenticato non vede le pagine pre-login, va al
 *  proprio atterraggio. Riusa `smistaRadice` — stessa logica che prima
 *  viveva solo su `/`. */
export function guardiaPubblico(ruolo: RuoloRealm | null): void {
  const dove = smistaRadice(ruolo);
  if (dove) throw redirect({ to: dove });
}

/** (servizi): richiede solo autenticazione, nessuna restrizione di ruolo
 *  (funzionario_adg, beneficiario_ri e i due admin vi accedono tutti). */
export function guardiaAutenticato(ruolo: RuoloRealm | null): void {
  if (!ruolo) throw redirect({ to: '/login/pa' });
}

/** (amministrazione): richiede platform_admin o tenant_admin. Chi non lo
 *  è (anonimo incluso) torna al proprio atterraggio, mai a una schermata
 *  d'errore — l'anonimo va comunque al login, non ha un ATTERRAGGIO. */
export function guardiaAmministrazione(ruolo: RuoloRealm | null): void {
  if (!ruolo) throw redirect({ to: '/login/pa' });
  if (ruolo !== 'platform_admin' && ruolo !== 'tenant_admin') {
    throw redirect({ to: ATTERRAGGIO[ruolo] });
  }
}
